import { useState } from "react";
import { EYEBROW_CLASS, NOTE_CLASS } from "../../lib/ui.js";
import { DEFAULT_MODE_COPY, MODEL_ALIAS_COPY } from "./claudeCode/copy.js";
import { INPUT_CLASS, ROW_ACTION, SECONDARY_ACTION } from "./styles.js";
import { Interaction, Mechanics, OnEndpoints } from "./textRoles.jsx";

export function SectionCard({ eyebrow, title, description, children }) {
    return (
        <section className="grid gap-[16px] rounded-[10px] border border-[color:var(--line)] bg-[color:var(--bg)] p-[14px]">
            <header className="grid gap-[2px]">
                <span className={EYEBROW_CLASS}>{eyebrow}</span>
                <h2 className="m-0 text-[15px] leading-[1.06] font-medium tracking-[-0.04em] text-[color:var(--ink)]">{title}</h2>
                {description ? <p className={`${NOTE_CLASS} mt-[4px] text-[12px] leading-[1.4]`}>{description}</p> : null}
            </header>
            {children}
        </section>
    );
}

export function ToggleControl({ checked, onChange, label, description, disabled }) {
    return (
        <div className="grid gap-[5px] border-t border-[color:var(--line)] pt-[10px] first:border-t-0 first:pt-0">
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                disabled={disabled}
                onClick={() => onChange(!checked)}
                className={[
                    "flex min-h-[32px] items-center justify-between gap-[12px] rounded-[7px] border px-[10px] text-left text-[12px] disabled:cursor-default disabled:opacity-60",
                    checked
                        ? "border-[color:var(--green)] bg-[color-mix(in_srgb,var(--ink)_10%,transparent)] text-[color:var(--ink)]"
                        : "border-[color:var(--line)] bg-transparent text-[color:var(--muted)]",
                ].join(" ")}
            >
                <span className="font-medium">{label}</span>
                <span className="inline-flex items-center gap-[6px] text-[10px]">
                    <span className={`h-[7px] w-[7px] rounded-full ${checked ? "bg-[color:var(--green)]" : "bg-[color:var(--muted-soft)]"}`} />
                    {checked ? "Enabled" : "Not enabled"}
                </span>
            </button>
            {description ? <Mechanics>{description}</Mechanics> : null}
        </div>
    );
}

export function ChoiceRow({ value, onChange, options, label, disabled }) {
    return (
        <div className="grid gap-[7px]">
            <span className="text-[11px] font-medium text-[color:var(--ink)]">{label}</span>
            {/* Tabs' track and raised pick, kept as radios. */}
            <div className="inline-flex flex-wrap items-center gap-[2px] justify-self-start rounded-[9px] bg-[color-mix(in_srgb,var(--ink)_6%,transparent)] p-[3px]" role="radiogroup" aria-label={label}>
                {options.map((option) => (
                    <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={value === option.value}
                        disabled={disabled}
                        onClick={() => onChange(option.value)}
                        className={[
                            "h-[26px] cursor-pointer whitespace-nowrap rounded-[6px] border-0 px-[11px] text-[12.5px] leading-none transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[color:var(--green)] disabled:cursor-default disabled:opacity-60",
                            value === option.value
                                ? "bg-[color-mix(in_srgb,var(--ink)_13%,transparent)] font-medium text-[color:var(--ink)]"
                                : "bg-transparent text-[color:var(--muted)] hover:text-[color:var(--ink)]",
                        ].join(" ")}
                    >
                        {option.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

export function StatusMark({ tone = "neutral", children }) {
    const dot = tone === "good" ? "bg-[color:var(--green)]" : tone === "warning" ? "bg-[#aa742f]" : tone === "danger" ? "bg-[#aa4c43]" : "bg-[color:var(--muted-soft)]";
    return <span className="inline-flex items-center gap-[6px] text-[10px] text-[color:var(--muted)]"><span className={`h-[7px] w-[7px] rounded-full ${dot}`} />{children}</span>;
}

export function ChoiceCard({ label, description, status, selected, disabled, role, onClick }) {
    return (
        <button
            type="button"
            role={role}
            aria-checked={selected}
            disabled={disabled}
            onClick={onClick}
            className={[
                "grid min-w-0 gap-[3px] rounded-[7px] border p-[9px] text-left disabled:cursor-default disabled:opacity-60",
                selected ? "border-[color:var(--green)] bg-[color-mix(in_srgb,var(--ink)_10%,transparent)]" : "border-[color:var(--line)] bg-transparent",
            ].join(" ")}
        >
            <span className="flex flex-wrap items-baseline justify-between gap-x-[8px] gap-y-[2px] text-[10px] font-medium text-[color:var(--ink)]">
                <span>{label}</span><span className="text-[color:var(--muted)]">{status}</span>
            </span>
            {description ? <span className={`${NOTE_CLASS} leading-[1.35]`}>{description}</span> : null}
        </button>
    );
}

export function PermissionModePicker({ modes, value, onChange, disabled, autoModeAllowed }) {
    const options = [{ value: "", label: "Not configured" }, ...modes];
    if (value && !options.some((mode) => mode.value === value)) {
        options.push({ value, label: value });
    }
    return (
        <div className="grid gap-[6px] md:grid-cols-2" role="radiogroup" aria-label="Starting permission mode">
            {options.map((mode) => {
                const selected = value === mode.value;
                const unavailable = mode.value === "auto" && !autoModeAllowed;
                const copy = DEFAULT_MODE_COPY[mode.value] ?? {
                    label: mode.label,
                    description: "",
                };
                const description = unavailable
                    ? "Auto is disabled. Permit it in Auto mode before choosing it as the starting mode."
                    : copy.description;
                return (
                    <ChoiceCard
                        key={mode.value}
                        role="radio"
                        label={copy.label}
                        description={description}
                        status={unavailable ? "Unavailable" : selected ? "Selected" : "Not selected"}
                        selected={selected}
                        disabled={disabled || unavailable}
                        onClick={() => onChange(mode.value)}
                    />
                );
            })}
        </div>
    );
}

export function ModelAliasPicker({ aliases, selected, onChange, disabled }) {
    const values = [...aliases, ...selected.filter((value) => !aliases.includes(value))];
    return (
        <div className="grid gap-[6px] md:grid-cols-2">
            {values.map((value) => {
                const checked = selected.includes(value);
                const copy = MODEL_ALIAS_COPY[value] ?? {
                    label: value,
                    description: "Imported custom model identifier. It will be preserved unless removed.",
                };
                return (
                    <ChoiceCard
                        key={value}
                        role="checkbox"
                        label={copy.label}
                        description={copy.description}
                        status={checked ? "Included" : "Not included"}
                        selected={checked}
                        disabled={disabled}
                        onClick={() => onChange(checked ? selected.filter((item) => item !== value) : [...selected, value])}
                    />
                );
            })}
        </div>
    );
}

export function ResourceList({ items, onRemove, onAdd, addLabel, placeholder, inputLabel, help, example, previewLabel, disabled, validate }) {
    const [draft, setDraft] = useState("");
    const [error, setError] = useState(null);
    const add = () => {
        const value = draft.trim();
        if (!value) return;
        const problem = validate?.(value) ?? null;
        setError(problem);
        if (problem) return;
        onAdd(value);
        setDraft("");
    };
    return (
        <div className="grid gap-[7px]">
            <div className="grid border-t border-[color:var(--line)]">
                {items.map((item) => (
                    <div key={item} className="grid min-h-[36px] grid-cols-[minmax(0,1fr)_auto] items-center gap-[8px] border-b border-[color:var(--line)]">
                        <code className="truncate text-[10px] text-[color:var(--ink)]">{item}</code>
                        {!disabled ? <button type="button" onClick={() => onRemove(item)} className={ROW_ACTION}>Remove</button> : null}
                    </div>
                ))}
            </div>
            {!disabled ? (
                <div className="grid gap-[6px]">
                    <span className="text-[11px] font-medium text-[color:var(--ink)]">{inputLabel}</span>
                    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-[6px]">
                        <input aria-label={inputLabel} aria-invalid={error ? true : undefined} className={INPUT_CLASS} value={draft} placeholder={placeholder} onChange={(event) => { setDraft(event.target.value); setError(null); }} onKeyDown={(event) => { if (event.key === "Enter") add(); }} />
                        <button type="button" className={SECONDARY_ACTION} onClick={add}>{addLabel}</button>
                    </div>
                    {error ? <p role="alert" className="m-0 text-[11px] text-[#aa4c43]">{error}</p> : null}
                    {previewLabel && draft.trim() ? <Mechanics>{previewLabel}: <code>{draft.trim()}</code></Mechanics> : null}
                    <Mechanics>{help}{example && example !== placeholder ? <> <span className="text-[color:var(--ink)]">Example: <code>{example}</code></span></> : null}</Mechanics>
                </div>
            ) : null}
        </div>
    );
}

export function AutoModeDefaultsNotice({ hasEntries, includesDefaults, onRestore, disabled, setting }) {
    if (!hasEntries) {
        return <OnEndpoints>Claude Code's built-in {setting} apply unchanged. No list is written until you add an entry.</OnEndpoints>;
    }
    if (!includesDefaults) {
        return (
            <div className="grid gap-[7px]">
                <Interaction scope="Claude Code built-ins">The saved array does not contain <code>$defaults</code>, so these entries replace the built-in {setting} rather than adding to them. Restore the built-ins before editing unless the replacement was deliberate.</Interaction>
                {!disabled ? <button type="button" className={`${SECONDARY_ACTION} justify-self-start`} onClick={onRestore}>Restore built-ins</button> : null}
            </div>
        );
    }
    return <OnEndpoints written>Claude Code's built-in {setting} still apply: the dashboard writes <code>$defaults</code> before your entries, so later built-in updates keep reaching endpoints.</OnEndpoints>;
}
